/**
 * Reset Password Modal
 *
 * Modal for resetting user password
 */

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/presentation/components/ui/Dialog'
import { Input } from '@/presentation/components/ui/Input'
import { Button } from '@/presentation/components/ui/Button'
import { useResetPassword } from '@/application/users/useUsers'
import { toast } from '@/lib/toast'
import type { UserWithInstitution } from '@/domain/user/types'

// =============================================================================
// Schema
// =============================================================================

const resetPasswordSchema = z
  .object({
    newPassword: z.string().min(8, 'La contraseña debe tener al menos 8 caracteres'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Las contraseñas no coinciden',
    path: ['confirmPassword'],
  })

type FormData = z.infer<typeof resetPasswordSchema>

// =============================================================================
// Component
// =============================================================================

interface ResetPasswordModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  user: UserWithInstitution | null
}

export function ResetPasswordModal({
  open,
  onOpenChange,
  user,
}: ResetPasswordModalProps) {
  const resetMutation = useResetPassword()

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
  })

  const onSubmit = async (data: FormData) => {
    if (!user) return

    resetMutation.mutate(
      {
        id: user.id,
        data: { newPassword: data.newPassword },
      },
      {
        onSuccess: () => {
          toast.success('Contraseña actualizada')
          reset()
          onOpenChange(false)
        },
        onError: () => toast.error('Error al actualizar contraseña'),
      }
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Cambiar Contraseña</DialogTitle>
          <DialogDescription>
            Establece una nueva contraseña para <strong>{user?.email}</strong>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 mt-4">
          <Input
            label="Nueva Contraseña"
            type="password"
            placeholder="********"
            error={errors.newPassword?.message}
            {...register('newPassword')}
          />

          <Input
            label="Confirmar Contraseña"
            type="password"
            placeholder="********"
            error={errors.confirmPassword?.message}
            {...register('confirmPassword')}
          />

          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={resetMutation.isPending}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={resetMutation.isPending}>
              {resetMutation.isPending ? 'Guardando...' : 'Cambiar Contraseña'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
